import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useLanguages } from '../../hooks/useLanguages';
import { SrsService } from '../../services/SrsService';
import { SoundEffectService } from '../../services/SoundEffectService';
import { ChunkyButton, GameSegmentBar, ShakeWidget } from '../../components/GameUI';

// Multiple-choice review of the cards the learner keeps getting wrong,
// regardless of whether they're due today — see SrsService.weakCards.
// Mirrors the mobile app's WeakWordsScreen.

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildQuestions(cards) {
  return cards.map((card) => {
    const wrongs = shuffle(
      Array.from(new Set(
        cards
          .filter((c) => c.wordKey !== card.wordKey && c.targetWord !== card.targetWord)
          .map((c) => c.targetWord),
      )),
    );
    const options = shuffle([card.targetWord, ...wrongs.slice(0, 3)]);
    return { card, options };
  });
}

export default function WeakWordsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { languagePrefs } = useAuth();
  const { languages } = useLanguages();
  const langMap = useMemo(() => Object.fromEntries(languages.map((l) => [l.code, l])), [languages]);
  const nativeLang = langMap[languagePrefs?.nativeLanguage] || { dir: 'ltr', label: '' };
  const targetLang = langMap[languagePrefs?.targetLanguage] || { dir: 'ltr', label: '' };
  const langPair = `${languagePrefs?.nativeLanguage || ''}_${languagePrefs?.targetLanguage || ''}`;

  const [questions, setQuestions] = useState(null);
  const [idx, setIdx] = useState(0);
  const [chosen, setChosen] = useState(null);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);

  function load() {
    const cards = SrsService.weakCards(langPair);
    setQuestions(buildQuestions(cards));
    setIdx(0);
    setChosen(null);
    setScore(0);
    setDone(false);
  }

  useEffect(load, [langPair]);

  function pick(opt) {
    if (!questions || chosen != null) return;
    const q = questions[idx];
    const correct = opt === q.card.targetWord;
    if (correct) {
      SoundEffectService.playCorrect();
      if (navigator.vibrate) navigator.vibrate(15);
    } else {
      SoundEffectService.playWrong();
      if (navigator.vibrate) navigator.vibrate(35);
    }
    SrsService.review(q.card, correct ? 5 : 1);
    setChosen(opt);
    if (correct) setScore((s) => s + 1);
  }

  function next() {
    if (!questions) return;
    if (idx + 1 >= questions.length) {
      setDone(true);
      return;
    }
    setIdx((i) => i + 1);
    setChosen(null);
  }

  const q = questions?.[idx];

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-lg px-4 py-6 sm:px-6">
        <div className="mb-4 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-500 shadow-sm hover:bg-slate-50">
            ← {t('practice.title')}
          </button>
          <h1 className="text-sm font-black text-slate-900">{t('practice.weakWords')}</h1>
          <span className="w-16" />
        </div>

        {!questions ? (
          <div className="flex justify-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" />
          </div>
        ) : questions.length === 0 ? (
          <div className="flex flex-col items-center py-16 text-center">
            <span className="text-5xl">💪</span>
            <p className="mt-4 max-w-xs text-sm text-slate-500">{t('games.weakWordsEmpty')}</p>
          </div>
        ) : done ? (
          <div className="flex flex-col items-center py-16 text-center">
            <span className="text-5xl">{score === questions.length ? '🏆' : score > questions.length / 2 ? '🎉' : '💪'}</span>
            <h2 className="mt-3 text-xl font-black text-slate-900">{t('games.weakWordsComplete')}</h2>
            <p className="mt-1 text-sm text-slate-500">{t('practice.correctScore', { score, total: questions.length })}</p>
            <div className="mt-8 w-full max-w-xs">
              <ChunkyButton color="#4F46E5" onClick={load} className="w-full py-4 text-center text-white">
                {t('games.tryAgain')}
              </ChunkyButton>
            </div>
          </div>
        ) : q ? (
          <div>
            <GameSegmentBar total={questions.length} current={idx} />
            <div className="mt-5 rounded-2xl bg-gradient-to-br from-indigo-50 to-violet-50 px-5 py-7 text-center">
              <p className="text-[10px] font-black uppercase tracking-widest text-indigo-600">{nativeLang.label}</p>
              <p dir={nativeLang.dir} className="mt-2 text-3xl font-black text-slate-900">{q.card.nativeWord}</p>
              <p className="mt-1.5 text-[11px] text-slate-400">{t('games.translateTo', { lang: targetLang.label })}</p>
            </div>

            <div dir={targetLang.dir} className="mt-4 grid grid-cols-2 gap-2.5">
              {q.options.map((opt) => {
                const revealed = chosen != null;
                const isCorrect = opt === q.card.targetWord;
                const isPicked = opt === chosen;
                let bg = '#F8FAFC', border = '#E2E8F0', fg = '#0F172A';
                if (revealed) {
                  if (isCorrect) { bg = '#D7FFB8'; border = '#58CC02'; fg = '#2B6B00'; }
                  else if (isPicked) { bg = '#FFDFE0'; border = '#FF4B4B'; fg = '#8B0000'; }
                  else { bg = '#FFFFFF'; border = '#F1F5F9'; fg = '#CBD5E1'; }
                }
                return (
                  <ShakeWidget key={opt} trigger={revealed && isPicked && !isCorrect}>
                    <button
                      onClick={() => pick(opt)}
                      disabled={revealed}
                      style={{ background: bg, borderColor: border, color: fg }}
                      className="flex w-full items-center justify-between rounded-2xl border-[2.5px] px-3.5 py-3 text-left text-sm font-black transition-colors"
                    >
                      <span className="truncate">{opt}</span>
                      {revealed && (isCorrect || isPicked) && (
                        <span className="ml-2 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs text-white" style={{ background: isCorrect ? '#58CC02' : '#FF4B4B' }}>
                          {isCorrect ? '✓' : '✕'}
                        </span>
                      )}
                    </button>
                  </ShakeWidget>
                );
              })}
            </div>

            {chosen != null && (
              <div className="mt-5">
                <ChunkyButton color="#58CC02" shadowColor="#4CAD02" onClick={next} className="w-full py-3.5 text-center text-white">
                  {idx + 1 >= questions.length ? t('practice.seeResults') : t('common.next')}
                </ChunkyButton>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
